import { Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import CartButton from '../../components/feature/CartButton';

// Blog articles data
const blogArticles = {
  '1': {
    category: 'Herbal Wisdom',
    date: 'March 15, 2025',
    readTime: '5 min read',
    title: 'The Power of Turmeric: Ancient Remedy for Modern Health',
    image: 'https://public.readdy.ai/ai/img_res/08e90ef8f6bb262d4f9f31fa636c37cc.jpg',
    relatedArticles: [2, 6, 7],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Turmeric, the golden spice that has been a cornerstone of Ayurvedic medicine for thousands of years, continues to captivate modern science with its remarkable health benefits. This vibrant root contains curcumin, a powerful compound that has been extensively studied for its therapeutic properties.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">The Science Behind the Spice</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Curcumin, the active ingredient in turmeric, is a potent anti-inflammatory and antioxidant compound. Research has shown that it can help reduce inflammation at the molecular level, potentially benefiting conditions ranging from arthritis to heart disease.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Traditional Uses in Ayurveda</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          In Ayurvedic tradition, turmeric has been used to support digestive health, promote healthy skin, and maintain overall vitality. Ancient practitioners recognized its warming properties and its ability to balance the body's natural systems.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Modern Applications</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Turmeric supplements are used to support joint health, boost immune function, and promote healthy aging. When combined with black pepper extract, the bioavailability of curcumin increases significantly, making it more effective.
        </p>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Key Benefits of Turmeric</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
              <span className="text-gray-600">Powerful anti-inflammatory properties</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
              <span className="text-gray-600">Strong antioxidant effects</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
              <span className="text-gray-600">Supports joint and muscle health</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
              <span className="text-gray-600">Promotes healthy digestion</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
              <span className="text-gray-600">May support brain health and cognitive function</span>
            </li>
          </ul>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">How to Incorporate Turmeric</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          While turmeric can be added to foods and beverages, supplementation ensures consistent, therapeutic doses. Look for high-quality supplements that include black pepper extract for enhanced absorption.
        </p>

        <p className="text-gray-600 leading-relaxed mb-6">
          As with any supplement, it's important to consult with a healthcare practitioner before beginning a turmeric regimen, especially if you're taking medications or have existing health conditions.
        </p>
      </>
    )
  },
  '2': {
    category: "Men's Health",
    date: 'March 18, 2025',
    readTime: '5 min read',
    title: 'Erectile Dysfunction: Causes and Natural Solutions',
    image: 'https://readdy.ai/api/search-image?query=stressed%20middle-aged%20man%20sitting%20on%20bed%20edge%20looking%20worried%20bedroom%20setting%20dim%20lighting%20depicting%20erectile%20dysfunction%20concern%20and%20relationship%20stress%20realistic%20medical%20photography&width=1200&height=600&seq=blog-ed-real-concern&orientation=landscape',
    relatedArticles: [3, 11, 6],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Erectile dysfunction is a common condition that has numerous possible causes. Treatments and remedies can depend on the underlying cause. It can be a difficult topic to discuss, but erectile dysfunction (ED) isn't uncommon. It affects an estimated 30 to 50 million men in the United States and nearly 322 million men globally.
        </p>

        <p className="text-gray-600 leading-relaxed mb-8">
          Making lifestyle changes can help improve your ED, but there are some factors you'll have to discuss with a doctor. Learn about the causes of ED, also known as impotence, and how you may be able to manage or prevent it.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Mental Factors Can Cause Problems</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          For some people, sex isn't as enjoyable as it could be. Factors like depression, stress, fatigue, and sleep disorders can contribute to ED by disrupting feelings of sexual excitement in the brain. While sex can be a stress reliever, ED can add to the stress you're already experiencing.
        </p>
        <p className="text-gray-600 leading-relaxed mb-6">
          Relationship problems can also contribute to ED. Arguments and bad communication can make the bedroom an uncomfortable place. This is why it's important for couples to communicate openly and honestly with each other.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Unhealthy Lifestyle Habits</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Tobacco use and other substance overuse can damage the lining of blood vessels and constrict them. Heavy alcohol consumption can make it harder for your body to dilate blood vessels and decrease blood pressure. Both can lead to or worsen ED. Cutting back or quitting entirely can help lessen or stop symptoms.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Overweight and Obesity</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Obesity is a common factor related to ED. Diabetes, high blood pressure, and heart disease are also tied to obesity and ED. These conditions pose significant health risks and can affect sexual performance.
        </p>
        <p className="text-gray-600 leading-relaxed mb-6">
          Cardiovascular exercise like swimming, running, and bicycling help shed pounds and increase oxygen and blood flow throughout the body, including your penis, improving symptoms and erectile function.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">ED as a Side Effect of Other Conditions</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          ED can be caused by several other physical problems besides obesity and weight-related diseases, including:
        </p>
        <ul className="space-y-2 mb-6 ml-6">
          <li className="text-gray-600">• Atherosclerosis, or clogged blood vessels</li>
          <li className="text-gray-600">• Low testosterone levels</li>
          <li className="text-gray-600">• Diabetes</li>
          <li className="text-gray-600">• Parkinson's disease</li>
          <li className="text-gray-600">• Multiple sclerosis</li>
          <li className="text-gray-600">• Metabolic syndrome</li>
        </ul>
        <p className="text-gray-600 leading-relaxed mb-6">
          Taking certain prescription medications can also lead to ED, such as:
        </p>
        <ul className="space-y-2 mb-6 ml-6">
          <li className="text-gray-600">• Blood pressure medications</li>
          <li className="text-gray-600">• Antihistamines</li>
          <li className="text-gray-600">• Antidepressants</li>
          <li className="text-gray-600">• Chemotherapy drugs</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Peyron's Disease and Surgery</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Peyron's disease involves abnormal curvature of the penis during an erection. This can cause ED as fibrous scar tissue develops under the skin of the penis. Other symptoms of Peyron's include pain during an erection and intercourse.
        </p>
        <p className="text-gray-600 leading-relaxed mb-6">
          Surgeries or injuries in the pelvic or lower spinal region can also cause ED. You may need medical treatment depending on the physical cause of your ED. Both medical and surgical treatments for prostate cancer or enlarged prostate can also cause ED.
        </p>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Erectile Dysfunction</h3>
          <p className="text-gray-600 leading-relaxed mb-6">
            There are several natural approaches to treating ED besides improving your lifestyle habits. Our Ayurvedic formulations offer holistic support for sexual health.
          </p>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-purple-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/1dd659f395c13bd2d90a61a82f7ae7a8.jpeg"
                  alt="FS Desire Capsule"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  FS Desire Capsule
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  FS‑DESIRE CAPSULES is a special formula with an exclusive blend of 14 Ayurvedic remedies. Designed to bring back love and wholistic healing to your sexual life, clear away impotency, bring an end to premature ejaculation and improve your strength.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(9,301 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦20,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-emerald-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/1ac0f230ef4066a4924563e20feb1190.jpeg"
                  alt="Vita Trace"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Vita Trace (Double Stemcell+)
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  An amazing combination that renews your cells, repairs damaged tissues, nerve muscles, and supports the reproductive system. Provides comprehensive rejuvenation for overall vitality and sexual health.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(98 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦29,900</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <p className="text-gray-600 leading-relaxed mt-6">
            However, if you're taking certain other medications or have specific cardiovascular diseases, contact our health consultant before starting any new supplement regimen.
          </p>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Takeaway</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Medical conditions, lifestyle habits, and stress or anxiety can all lead to ED; however, this condition can often be treated naturally. If ED is affecting your day-to-day life, make an appointment with a medical professional to receive a diagnosis. Your doctor will then be able to suggest the best treatments for you.
        </p>
      </>
    )
  },
  '3': {
    category: 'Weight Management',
    date: 'March 20, 2025',
    readTime: '7 min read',
    title: 'Natural Weight Loss: Ayurvedic Approach to Healthy Weight Management',
    image: 'https://readdy.ai/api/search-image?query=overweight%20person%20standing%20on%20scale%20looking%20concerned%20surrounded%20by%20unhealthy%20junk%20food%20and%20measuring%20tape%20realistic%20lifestyle%20photography%20depicting%20weight%20loss%20struggle%20and%20health%20concern%20natural%20lighting&width=1200&height=600&seq=blog-weight-real-concern&orientation=landscape',
    relatedArticles: [4, 7, 2],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Weight management is one of the most common health concerns today. While there are countless diet plans and weight loss programs available, Ayurvedic medicine offers a holistic approach that addresses the root causes of weight gain and supports sustainable, healthy weight loss.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Understanding Weight Gain</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Weight gain occurs when your body takes in more calories than it burns. However, Ayurveda recognizes that weight gain is often a symptom of deeper imbalances in your body's systems, including slow metabolism, poor digestion, hormonal imbalances, and toxin accumulation.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Causes of Weight Gain</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Slow metabolism and poor digestive fire</li>
          <li className="text-gray-600">• Hormonal imbalances (thyroid, insulin resistance)</li>
          <li className="text-gray-600">• Sedentary lifestyle and lack of exercise</li>
          <li className="text-gray-600">• Poor diet high in processed foods and sugar</li>
          <li className="text-gray-600">• Stress and emotional eating</li>
          <li className="text-gray-600">• Lack of sleep and irregular eating patterns</li>
          <li className="text-gray-600">• Toxin accumulation in the body</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Weight Management</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-yellow-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/58c17aea15cb87831f729d39d912966a.jpeg"
                  alt="Dynamic Slim Juice"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Dynamic Slim Juice
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  An excellent Ayurvedic herbal product for weight loss. Prepared with Medohar Guggulu, it helps burn fat, boost metabolism, suppress appetite, and support sustainable weight loss while maintaining energy levels.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(143 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦30,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-rose-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6e120bfc8043ea77825d5145e57e2953.jpeg"
                  alt="Clean Detox"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Clean Detox
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Stimulates digestive fire, promotes bile flow, and helps in liver detoxification. Useful in burning stomach fats and cleansing your whole body for better weight management.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(127 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦28,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-lime-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6ddc54688ef6a5c29d09201b6c0c9f96.jpeg"
                  alt="Toxem Capsule"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Toxem Capsule
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  An Ayurvedic detoxifying formula with over 16 herbs. Eliminates toxins, supports liver function, and promotes overall body purification for better metabolism and weight management.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(112 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦38,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Healthy Weight Loss</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Eat a balanced diet rich in whole foods, vegetables, and lean proteins</li>
          <li className="text-gray-600">• Exercise regularly (combination of cardio and strength training)</li>
          <li className="text-gray-600">• Drink plenty of water throughout the day</li>
          <li className="text-gray-600">• Get adequate sleep (7-9 hours per night)</li>
          <li className="text-gray-600">• Manage stress through yoga, meditation, or relaxation techniques</li>
          <li className="text-gray-600">• Eat mindfully and avoid emotional eating</li>
          <li className="text-gray-600">• Avoid processed foods, sugar, and excessive carbohydrates</li>
          <li className="text-gray-600">• Practice portion control</li>
        </ul>

        <p className="text-gray-600 leading-relaxed mb-6">
          Sustainable weight loss requires a holistic approach that addresses diet, exercise, stress management, and metabolic health. By combining Ayurvedic herbal support with healthy lifestyle practices, you can achieve and maintain your ideal weight naturally.
        </p>
      </>
    )
  },
  '4': {
    category: 'Blood Sugar & Diabetes',
    date: 'March 22, 2025',
    readTime: '8 min read',
    title: 'Managing Diabetes Naturally: Ayurvedic Approach to Blood Sugar Control',
    image: 'https://readdy.ai/api/search-image?query=person%20checking%20blood%20glucose%20meter%20at%20breakfast%20table%20with%20healthy%20meal%20insulin%20pen%20and%20diabetes%20medication%20realistic%20medical%20lifestyle%20photography%20morning%20natural%20lighting&width=1200&height=600&seq=blog-diabetes-realistic-management&orientation=landscape',
    relatedArticles: [3, 5, 7],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Diabetes is a chronic condition that affects how your body processes blood sugar (glucose). While modern medicine offers various treatments, Ayurvedic medicine provides natural approaches to support healthy blood sugar levels and manage diabetes holistically.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Understanding Diabetes</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Diabetes occurs when your body either doesn't produce enough insulin (Type 1) or can't effectively use the insulin it produces (Type 2). This leads to elevated blood sugar levels, which can cause serious health complications if left unmanaged.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Causes of Diabetes</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Genetics:</strong> Family history of diabetes increases risk</li>
          <li className="text-gray-600">• <strong>Obesity:</strong> Excess body weight, especially around the abdomen</li>
          <li className="text-gray-600">• <strong>Sedentary Lifestyle:</strong> Lack of physical activity</li>
          <li className="text-gray-600">• <strong>Poor Diet:</strong> High consumption of processed foods, sugar, and refined carbohydrates</li>
          <li className="text-gray-600">• <strong>Age:</strong> Risk increases after age 45</li>
          <li className="text-gray-600">• <strong>Stress:</strong> Chronic stress affects blood sugar regulation</li>
          <li className="text-gray-600">• <strong>Hormonal Imbalances:</strong> Conditions affecting insulin production</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Effects and Complications of Diabetes</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Cardiovascular disease and heart problems</li>
          <li className="text-gray-600">• Nerve damage (neuropathy) causing pain and numbness</li>
          <li className="text-gray-600">• Kidney damage (nephropathy)</li>
          <li className="text-gray-600">• Eye damage and vision problems</li>
          <li className="text-gray-600">• Slow wound healing and increased infection risk</li>
          <li className="text-gray-600">• Foot problems and potential amputation</li>
          <li className="text-gray-600">• Skin conditions</li>
          <li className="text-gray-600">• Hearing impairment</li>
          <li className="text-gray-600">• Depression and cognitive decline</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Diabetes Management</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-rose-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6e120bfc8043ea77825d5145e57e2953.jpeg"
                  alt="Clean Detox"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Clean Detox
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Fortified with Gilloy, which is highly effective for blood sugar control. Helps activate the pancreas, manage diabetes naturally, and detoxify your body while supporting healthy glucose metabolism.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(127 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦28,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-emerald-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/1ac0f230ef4066a4924563e20feb1190.jpeg"
                  alt="Vita Trace"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Vita Trace (Double Stemcell+)
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Provides wholistic rejuvenation and helps repair damaged tissues and cells. Supports overall metabolic health and strengthens the body's natural healing processes for better diabetes management.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(98 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦29,900</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Diabetes Management</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Follow a balanced diet low in refined carbohydrates and sugar</li>
          <li className="text-gray-600">• Exercise regularly (at least 30 minutes daily)</li>
          <li className="text-gray-600">• Monitor blood sugar levels regularly</li>
          <li className="text-gray-600">• Maintain a healthy weight</li>
          <li className="text-gray-600">• Manage stress through yoga, meditation, or relaxation techniques</li>
          <li className="text-gray-600">• Get adequate sleep (7-9 hours per night)</li>
          <li className="text-gray-600">• Stay hydrated</li>
          <li className="text-gray-600">• Avoid smoking and excessive alcohol</li>
          <li className="text-gray-600">• Take medications as prescribed by your doctor</li>
        </ul>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> While Ayurvedic remedies can support diabetes management, they should complement, not replace, medical treatment. Always consult with your healthcare provider before making changes to your diabetes management plan.
          </p>
        </div>

        <p className="text-gray-600 leading-relaxed mb-6">
          By combining Ayurvedic herbal support with proper diet, exercise, and medical care, you can effectively manage diabetes and reduce the risk of complications.
        </p>
      </>
    )
  },
  '5': {
    category: 'Heart Health',
    date: 'March 24, 2025',
    readTime: '7 min read',
    title: 'Managing High Blood Pressure Naturally: Ayurvedic Solutions for Heart Health',
    image: 'https://readdy.ai/api/search-image?query=doctor%20checking%20patient%20blood%20pressure%20with%20sphygmomanometer%20in%20medical%20office%20patient%20looking%20concerned%20hypertension%20monitoring%20realistic%20healthcare%20photography%20professional%20setting&width=1200&height=600&seq=blog-bp-realistic-checkup&orientation=landscape',
    relatedArticles: [4, 6, 7],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          High blood pressure (hypertension) is often called the "silent killer" because it typically has no symptoms but can lead to serious health complications. Ayurvedic medicine offers natural approaches to support cardiovascular health and maintain healthy blood pressure levels.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Understanding High Blood Pressure</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Blood pressure is the force of blood pushing against artery walls. When this pressure remains consistently high, it strains your heart and blood vessels, increasing the risk of heart disease, stroke, and other serious conditions.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Causes of High Blood Pressure</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Poor Diet:</strong> High sodium intake, processed foods, and unhealthy fats</li>
          <li className="text-gray-600">• <strong>Obesity:</strong> Excess body weight increases heart workload</li>
          <li className="text-gray-600">• <strong>Lack of Exercise:</strong> Sedentary lifestyle weakens cardiovascular system</li>
          <li className="text-gray-600">• <strong>Stress:</strong> Chronic stress elevates blood pressure</li>
          <li className="text-gray-600">• <strong>Smoking:</strong> Damages blood vessels and raises blood pressure</li>
          <li className="text-gray-600">• <strong>Excessive Alcohol:</strong> Can raise blood pressure over time</li>
          <li className="text-gray-600">• <strong>Genetics:</strong> Family history increases risk</li>
          <li className="text-gray-600">• <strong>Age:</strong> Risk increases with age</li>
          <li className="text-gray-600">• <strong>Kidney Disease:</strong> Affects blood pressure regulation</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Effects and Complications</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Heart attack and heart failure</li>
          <li className="text-gray-600">• Stroke and brain damage</li>
          <li className="text-gray-600">• Kidney damage and failure</li>
          <li className="text-gray-600">• Vision loss and eye problems</li>
          <li className="text-gray-600">• Aneurysm (weakened blood vessel walls)</li>
          <li className="text-gray-600">• Metabolic syndrome</li>
          <li className="text-gray-600">• Memory and cognitive problems</li>
          <li className="text-gray-600">• Sexual dysfunction</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Blood Pressure Management</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-indigo-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/2155e4a7cb97e860d597e291c8497181.jpeg"
                  alt="Noni Juice"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Noni Juice
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Traditionally used to treat high blood pressure and heart diseases. Helps normalize blood pressure, improve circulation, and reduce cholesterol and triglycerides naturally. Fortified with Brahmi and Alma for enhanced cardiovascular support.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(156 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦32,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-rose-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6e120bfc8043ea77825d5145e57e2953.jpeg"
                  alt="Clean Detox"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Clean Detox
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Supports liver detoxification and helps cleanse your whole body. Promotes healthy circulation and fights free radicals that can damage blood vessels, supporting overall cardiovascular health.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(127 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦28,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Healthy Blood Pressure</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Reduce sodium intake (less than 2,300mg per day)</li>
          <li className="text-gray-600">• Eat a heart-healthy diet rich in fruits, vegetables, and whole grains</li>
          <li className="text-gray-600">• Exercise regularly (at least 30 minutes most days)</li>
          <li className="text-gray-600">• Maintain a healthy weight</li>
          <li className="text-gray-600">• Limit alcohol consumption</li>
          <li className="text-gray-600">• Quit smoking</li>
          <li className="text-gray-600">• Manage stress through relaxation techniques</li>
          <li className="text-gray-600">• Get adequate sleep</li>
          <li className="text-gray-600">• Monitor your blood pressure regularly</li>
          <li className="text-gray-600">• Take prescribed medications as directed</li>
        </ul>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> High blood pressure requires medical supervision. While Ayurvedic remedies can support cardiovascular health, always consult with your healthcare provider and continue taking prescribed medications.
          </p>
        </div>

        <p className="text-gray-600 leading-relaxed mb-6">
          By combining natural Ayurvedic support with healthy lifestyle changes and medical care, you can effectively manage blood pressure and protect your heart health.
        </p>
      </>
    )
  },
  '6': {
    category: 'Immunity',
    date: 'March 26, 2025',
    readTime: '6 min read',
    title: 'Boosting Your Immune System: Natural Defense Against Infections and Diseases',
    image: 'https://readdy.ai/api/search-image?query=person%20with%20cold%20and%20flu%20symptoms%20holding%20tissue%20box%20surrounded%20by%20medicine%20bottles%20looking%20unwell%20at%20home%20depicting%20weak%20immune%20system%20realistic%20medical%20lifestyle%20photography&width=1200&height=600&seq=blog-immunity-realistic-illness&orientation=landscape',
    relatedArticles: [7, 5, 4],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Your immune system is your body's natural defense against infections, diseases, and harmful pathogens. A strong immune system is essential for overall health and well-being. Ayurvedic medicine offers powerful natural approaches to strengthen immunity and protect your body.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Understanding the Immune System</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          The immune system is a complex network of cells, tissues, and organs that work together to defend your body against harmful invaders like bacteria, viruses, parasites, and fungi. When your immune system is weak, you become more susceptible to infections and diseases.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Causes of Weak Immunity</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Poor Nutrition:</strong> Lack of essential vitamins and minerals</li>
          <li className="text-gray-600">• <strong>Chronic Stress:</strong> Weakens immune response</li>
          <li className="text-gray-600">• <strong>Lack of Sleep:</strong> Insufficient rest impairs immune function</li>
          <li className="text-gray-600">• <strong>Sedentary Lifestyle:</strong> Lack of physical activity</li>
          <li className="text-gray-600">• <strong>Smoking and Alcohol:</strong> Damage immune cells</li>
          <li className="text-gray-600">• <strong>Chronic Diseases:</strong> Diabetes, HIV, cancer weaken immunity</li>
          <li className="text-gray-600">• <strong>Medications:</strong> Some drugs suppress immune function</li>
          <li className="text-gray-600">• <strong>Age:</strong> Immune function declines with age</li>
          <li className="text-gray-600">• <strong>Environmental Toxins:</strong> Pollution and chemicals</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Effects of Weak Immunity</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Frequent colds, flu, and infections</li>
          <li className="text-gray-600">• Slow wound healing</li>
          <li className="text-gray-600">• Chronic fatigue and low energy</li>
          <li className="text-gray-600">• Digestive problems</li>
          <li className="text-gray-600">• Skin infections and rashes</li>
          <li className="text-gray-600">• Autoimmune disorders</li>
          <li className="text-gray-600">• Increased risk of serious diseases</li>
          <li className="text-gray-600">• Prolonged illness recovery time</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Immune Support</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-emerald-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/1ac0f230ef4066a4924563e20feb1190.jpeg"
                  alt="Vita Trace"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Vita Trace (Double Stemcell+)
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Combines multiple fruit stem cells with 16 Ayurvedic remedies to boost immune system, repair damaged tissues, and provide wholistic rejuvenation. Strengthens your body's natural defenses against infections and diseases.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(98 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦29,900</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-indigo-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/2155e4a7cb97e860d597e291c8497181.jpeg"
                  alt="Noni Juice"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Noni Juice
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Contains powerful antioxidants that strengthen immune function and protect against cellular damage. Helps prevent infections and supports overall health and vitality.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(156 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦32,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Strong Immunity</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Eat a balanced diet rich in fruits, vegetables, and whole grains</li>
          <li className="text-gray-600">• Exercise regularly (at least 30 minutes daily)</li>
          <li className="text-gray-600">• Get adequate sleep (7-9 hours per night)</li>
          <li className="text-gray-600">• Manage stress through meditation, yoga, or relaxation</li>
          <li className="text-gray-600">• Stay hydrated</li>
          <li className="text-gray-600">• Avoid smoking and excessive alcohol</li>
          <li className="text-gray-600">• Maintain good hygiene practices</li>
          <li className="text-gray-600">• Get regular health check-ups</li>
          <li className="text-gray-600">• Spend time outdoors in sunlight for vitamin D</li>
          <li className="text-gray-600">• Maintain healthy relationships and social connections</li>
        </ul>

        <p className="text-gray-600 leading-relaxed mb-6">
          By combining Ayurvedic immune-boosting herbs with healthy lifestyle practices, you can strengthen your body's natural defenses and protect yourself against infections and diseases.
        </p>
      </>
    )
  },
  '7': {
    category: 'Detox & Cleanse',
    date: 'March 28, 2025',
    readTime: '7 min read',
    title: 'Body Detoxification: Cleanse Your System Naturally with Ayurvedic Wisdom',
    image: 'https://readdy.ai/api/search-image?query=person%20looking%20tired%20and%20bloated%20holding%20stomach%20with%20digestive%20discomfort%20surrounded%20by%20unhealthy%20processed%20foods%20depicting%20toxin%20buildup%20realistic%20lifestyle%20photography%20natural%20lighting&width=1200&height=600&seq=blog-detox-realistic-need&orientation=landscape',
    relatedArticles: [3, 6, 4],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          In today's world, we're constantly exposed to toxins from processed foods, pollution, chemicals, and stress. These toxins accumulate in our bodies and can lead to various health problems. Ayurvedic detoxification offers natural methods to cleanse your system and restore optimal health.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Understanding Toxin Buildup</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Toxins are harmful substances that can damage cells, organs, and body systems. When your body's natural detoxification systems (liver, kidneys, digestive system) become overwhelmed, toxins accumulate and cause health problems.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Sources of Toxins</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Processed Foods:</strong> Artificial additives, preservatives, and chemicals</li>
          <li className="text-gray-600">• <strong>Environmental Pollution:</strong> Air pollution, pesticides, heavy metals</li>
          <li className="text-gray-600">• <strong>Medications:</strong> Long-term drug use</li>
          <li className="text-gray-600">• <strong>Alcohol and Smoking:</strong> Introduce harmful toxins</li>
          <li className="text-gray-600">• <strong>Stress:</strong> Produces stress hormones and free radicals</li>
          <li className="text-gray-600">• <strong>Poor Water Quality:</strong> Contaminated drinking water</li>
          <li className="text-gray-600">• <strong>Household Chemicals:</strong> Cleaning products, cosmetics</li>
          <li className="text-gray-600">• <strong>Metabolic Waste:</strong> Natural byproducts of body processes</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Signs You Need Detoxification</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Chronic fatigue and low energy</li>
          <li className="text-gray-600">• Digestive problems (bloating, constipation, gas)</li>
          <li className="text-gray-600">• Skin problems (acne, rashes, dull complexion)</li>
          <li className="text-gray-600">• Headaches and brain fog</li>
          <li className="text-gray-600">• Weight gain or difficulty losing weight</li>
          <li className="text-gray-600">• Bad breath and body odor</li>
          <li className="text-gray-600">• Frequent infections</li>
          <li className="text-gray-600">• Joint and muscle pain</li>
          <li className="text-gray-600">• Mood swings and irritability</li>
          <li className="text-gray-600">• Sleep problems</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Detoxification Solutions</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-rose-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6e120bfc8043ea77825d5145e57e2953.jpeg"
                  alt="Clean Detox"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Clean Detox
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Age-old Ayurvedic formulation designed to stimulate digestive fire, promote bile flow, and support liver detoxification. Cleanses your whole body, fights free radicals, and helps eliminate accumulated toxins naturally.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(127 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦28,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-lime-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6ddc54688ef6a5c29d09201b6c0c9f96.jpeg"
                  alt="Toxem Capsule"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Toxem Capsule
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  An Ayurvedic detoxifying formula with over 16 herbs. Eliminates toxins, supports liver function, promotes overall body purification, and helps restore optimal health and vitality.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(112 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦38,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Natural Detoxification</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Drink plenty of water (8-10 glasses daily)</li>
          <li className="text-gray-600">• Eat organic, whole foods and avoid processed foods</li>
          <li className="text-gray-600">• Include fiber-rich foods to support digestion</li>
          <li className="text-gray-600">• Exercise regularly to promote sweating and circulation</li>
          <li className="text-gray-600">• Get adequate sleep for cellular repair</li>
          <li className="text-gray-600">• Practice deep breathing exercises</li>
          <li className="text-gray-600">• Reduce stress through meditation or yoga</li>
          <li className="text-gray-600">• Limit alcohol and avoid smoking</li>
          <li className="text-gray-600">• Use natural cleaning and personal care products</li>
          <li className="text-gray-600">• Consider periodic fasting or juice cleanses</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Benefits of Regular Detoxification</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Increased energy and vitality</li>
          <li className="text-gray-600">• Improved digestion and nutrient absorption</li>
          <li className="text-gray-600">• Clearer, healthier skin</li>
          <li className="text-gray-600">• Better mental clarity and focus</li>
          <li className="text-gray-600">• Stronger immune system</li>
          <li className="text-gray-600">• Weight loss and better metabolism</li>
          <li className="text-gray-600">• Reduced inflammation</li>
          <li className="text-gray-600">• Better sleep quality</li>
          <li className="text-gray-600">• Enhanced overall well-being</li>
        </ul>

        <p className="text-gray-600 leading-relaxed mb-6">
          Regular detoxification using Ayurvedic herbs combined with healthy lifestyle practices can help you eliminate toxins, restore balance, and achieve optimal health naturally.
        </p>
      </>
    )
  },
  '8': {
    category: 'Cancer Prevention',
    date: 'March 30, 2025',
    readTime: '9 min read',
    title: 'Cancer Prevention: Natural Support and Ayurvedic Approach to Cellular Health',
    image: 'https://readdy.ai/api/search-image?query=cancer%20patient%20with%20supportive%20family%20member%20holding%20hands%20in%20hospital%20consultation%20room%20pink%20ribbon%20cancer%20awareness%20symbol%20hope%20and%20strength%20realistic%20healthcare%20photography%20compassionate%20setting&width=1200&height=600&seq=blog-cancer-realistic-support&orientation=landscape',
    relatedArticles: [6, 7, 5],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Cancer is one of the leading causes of death worldwide. While there's no guaranteed way to prevent cancer, research shows that lifestyle choices and natural approaches can significantly reduce your risk. Ayurvedic medicine offers powerful herbs and practices that support cellular health and may help prevent cancer.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Understanding Cancer</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Cancer occurs when abnormal cells grow uncontrollably and spread to other parts of the body. These cells can form tumors and interfere with normal body functions. Cancer can affect any part of the body and has many different types.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Risk Factors for Cancer</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Tobacco Use:</strong> Leading cause of lung and other cancers</li>
          <li className="text-gray-600">• <strong>Poor Diet:</strong> Processed foods, red meat, low fiber intake</li>
          <li className="text-gray-600">• <strong>Obesity:</strong> Increases risk of multiple cancer types</li>
          <li className="text-gray-600">• <strong>Lack of Exercise:</strong> Sedentary lifestyle</li>
          <li className="text-gray-600">• <strong>Excessive Alcohol:</strong> Increases cancer risk</li>
          <li className="text-gray-600">• <strong>Sun Exposure:</strong> UV radiation causes skin cancer</li>
          <li className="text-gray-600">• <strong>Environmental Toxins:</strong> Chemicals, pollution, radiation</li>
          <li className="text-gray-600">• <strong>Chronic Infections:</strong> HPV, hepatitis, H. pylori</li>
          <li className="text-gray-600">• <strong>Genetics:</strong> Family history increases risk</li>
          <li className="text-gray-600">• <strong>Age:</strong> Risk increases with age</li>
          <li className="text-gray-600">• <strong>Chronic Inflammation:</strong> Long-term inflammation</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">How Cancer Affects the Body</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Uncontrolled cell growth and tumor formation</li>
          <li className="text-gray-600">• Spread to other organs (metastasis)</li>
          <li className="text-gray-600">• Organ dysfunction and failure</li>
          <li className="text-gray-600">• Weakened immune system</li>
          <li className="text-gray-600">• Severe pain and discomfort</li>
          <li className="text-gray-600">• Weight loss and malnutrition</li>
          <li className="text-gray-600">• Fatigue and weakness</li>
          <li className="text-gray-600">• Emotional and psychological impact</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Support for Cancer Prevention</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-indigo-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/2155e4a7cb97e860d597e291c8497181.jpeg"
                  alt="Noni Juice"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Noni Juice
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Helps prevent cellular damage caused by smoking and environmental toxins. Contains powerful antioxidants that may help prevent cancer and protect cells from damage. Fortified with Brahmi and Alma for enhanced cellular protection.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(156 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦32,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-emerald-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/1ac0f230ef4066a4924563e20feb1190.jpeg"
                  alt="Vita Trace"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Vita Trace (Double Stemcell+)
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Combines multiple fruit stem cells with 16 Ayurvedic remedies to repair damaged tissues and cells. Boosts immune system and provides wholistic rejuvenation to support your body's natural defense against abnormal cell growth.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(98 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦29,900</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Cancer Prevention</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Don't use tobacco in any form</li>
          <li className="text-gray-600">• Eat a healthy diet rich in fruits, vegetables, and whole grains</li>
          <li className="text-gray-600">• Maintain a healthy weight</li>
          <li className="text-gray-600">• Exercise regularly (at least 30 minutes daily)</li>
          <li className="text-gray-600">• Limit alcohol consumption</li>
          <li className="text-gray-600">• Protect yourself from sun exposure</li>
          <li className="text-gray-600">• Get vaccinated (HPV, hepatitis B)</li>
          <li className="text-gray-600">• Avoid risky behaviors (unsafe sex, sharing needles)</li>
          <li className="text-gray-600">• Get regular medical screenings</li>
          <li className="text-gray-600">• Reduce exposure to environmental toxins</li>
          <li className="text-gray-600">• Manage stress effectively</li>
          <li className="text-gray-600">• Get adequate sleep</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Cancer-Fighting Foods</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Cruciferous vegetables (broccoli, cauliflower, cabbage)</li>
          <li className="text-gray-600">• Berries (rich in antioxidants)</li>
          <li className="text-gray-600">• Tomatoes (lycopene)</li>
          <li className="text-gray-600">• Green tea (catechins)</li>
          <li className="text-gray-600">• Turmeric (curcumin)</li>
          <li className="text-gray-600">• Garlic and onions</li>
          <li className="text-gray-600">• Leafy green vegetables</li>
          <li className="text-gray-600">• Nuts and seeds</li>
          <li className="text-gray-600">• Fatty fish (omega-3)</li>
        </ul>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> While Ayurvedic remedies and lifestyle changes can support cancer prevention, they should not replace medical screening, diagnosis, or treatment. If you have cancer or suspect you might, consult with qualified healthcare professionals immediately.
          </p>
        </div>

        <p className="text-gray-600 leading-relaxed mb-6">
          By combining Ayurvedic herbal support with healthy lifestyle choices and regular medical screenings, you can significantly reduce your cancer risk and support your body's natural defenses against abnormal cell growth.
        </p>
      </>
    )
  },
  '9': {
    category: 'Dental Health',
    date: 'April 1, 2025',
    readTime: '6 min read',
    title: 'Natural Relief for Toothache and Dental Problems: Ayurvedic Solutions',
    image: 'https://readdy.ai/api/search-image?query=person%20holding%20jaw%20in%20pain%20with%20natural%20herbal%20remedies%20clove%20oil%20neem%20sticks%20and%20ayurvedic%20dental%20care%20products%20on%20clean%20white%20surface%20soft%20natural%20lighting%20emphasizing%20dental%20health%20and%20natural%20pain%20relief%20professional%20health%20photography&width=1200&height=600&seq=blog-dental&orientation=landscape',
    relatedArticles: [10, 7, 6],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Dental problems like toothaches, gum disease, and tooth sensitivity can be incredibly painful and disruptive to daily life. While modern dentistry offers effective treatments, Ayurvedic medicine provides natural remedies that can help manage dental pain, support oral health, and prevent future problems.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Dental Problems</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Toothache:</strong> Pain caused by tooth decay, infection, or damaged teeth</li>
          <li className="text-gray-600">• <strong>Gum Disease:</strong> Inflammation and infection of the gums (gingivitis and periodontitis)</li>
          <li className="text-gray-600">• <strong>Tooth Sensitivity:</strong> Pain when consuming hot, cold, or sweet foods</li>
          <li className="text-gray-600">• <strong>Tooth Decay:</strong> Cavities caused by bacteria and poor oral hygiene</li>
          <li className="text-gray-600">• <strong>Dental Abscess:</strong> Pus-filled infection at the tooth root</li>
          <li className="text-gray-600">• <strong>Bad Breath:</strong> Persistent unpleasant odor from the mouth</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Causes of Dental Problems</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Poor oral hygiene and irregular brushing</li>
          <li className="text-gray-600">• High sugar and acidic food consumption</li>
          <li className="text-gray-600">• Bacterial infections</li>
          <li className="text-gray-600">• Vitamin and mineral deficiencies (especially calcium and vitamin D)</li>
          <li className="text-gray-600">• Smoking and tobacco use</li>
          <li className="text-gray-600">• Grinding teeth (bruxism)</li>
          <li className="text-gray-600">• Hormonal changes</li>
          <li className="text-gray-600">• Certain medications that reduce saliva production</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Ayurvedic Perspective on Dental Health</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          In Ayurveda, dental problems are often linked to imbalances in the doshas and accumulation of toxins. Ayurvedic dental care emphasizes prevention through proper oral hygiene, herbal remedies, and dietary practices that support strong teeth and healthy gums.
        </p>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Dental Health</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-cyan-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6ddc54688ef6a5c29d09201b6c0c9f96.jpeg"
                  alt="Behar 3 capsule"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Behar-3 Capsules
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Anti-inflammatory power that protects against chronic conditions. Reduces inflammation in gums and oral tissues, supports healing, and provides natural pain relief for dental problems.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(967 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦28,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/2155e4a7cb97e860d597e291c8497181.jpeg"
                  alt="Nutri F"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Nutri F (Virus Defence)
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Builds body resistance against infections. Strengthens immune system to fight bacterial infections in the mouth and supports overall oral health.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(67 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦28,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Traditional Ayurvedic Dental Care Practices</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">
            <strong>Oil Pulling:</strong> Swish coconut or sesame oil in your mouth for 10-15 minutes daily
          </li>
          <li className="text-gray-600">
            <strong>Neem Sticks:</strong> Traditional tooth-cleaning twigs with antibacterial properties
          </li>
          <li className="text-gray-600">
            <strong>Clove Oil:</strong> Natural pain reliever for toothaches
          </li>
          <li className="text-gray-600">
            <strong>Turmeric Paste:</strong> Anti-inflammatory for gum problems
          </li>
          <li className="text-gray-600">
            <strong>Salt Water Rinse:</strong> Natural disinfectant for oral infections
          </li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Dental Health</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Brush teeth twice daily with fluoride toothpaste</li>
          <li className="text-gray-600">• Floss daily to remove plaque between teeth</li>
          <li className="text-gray-600">• Limit sugary and acidic foods</li>
          <li className="text-gray-600">• Eat calcium-rich foods for strong teeth</li>
          <li className="text-gray-600">• Stay hydrated to maintain saliva production</li>
          <li className="text-gray-600">• Visit your dentist regularly for check-ups</li>
          <li className="text-gray-600">• Avoid tobacco and excessive alcohol</li>
          <li className="text-gray-600">• Use a soft-bristled toothbrush</li>
        </ul>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> While Ayurvedic remedies can provide relief and support oral health, they should not replace professional dental care. If you have severe tooth pain, swelling, or signs of infection, consult a dentist immediately.
          </p>
        </div>

        <p className="text-gray-600 leading-relaxed mb-6">
          By combining Ayurvedic herbal support with good oral hygiene practices and regular dental check-ups, you can maintain healthy teeth and gums throughout your life.
        </p>
      </>
    )
  },
  '10': {
    category: 'Joint & Bone Health',
    date: 'April 3, 2025',
    readTime: '8 min read',
    title: 'Joint Pain, Arthritis & Osteoporosis: Natural Ayurvedic Solutions for Bone Health',
    image: 'https://readdy.ai/api/search-image?query=elderly%20person%20holding%20painful%20knee%20with%20visible%20discomfort%20sitting%20on%20chair%20arthritis%20joint%20pain%20realistic%20medical%20photography%20depicting%20bone%20and%20joint%20health%20concern%20natural%20lighting&width=1200&height=600&seq=blog-joint-realistic-arthritis&orientation=landscape',
    relatedArticles: [9, 5, 6],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Joint pain, arthritis, and osteoporosis are common conditions that affect millions of people, especially as they age. These conditions can significantly impact quality of life, causing pain, stiffness, and reduced mobility. Ayurvedic medicine offers natural approaches to manage these conditions and support bone and joint health.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Understanding Joint and Bone Problems</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          <strong>Arthritis</strong> is inflammation of the joints causing pain and stiffness. The most common types are osteoarthritis (wear and tear) and rheumatoid arthritis (autoimmune). <strong>Osteoporosis</strong> is a condition where bones become weak and brittle, increasing fracture risk.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Causes</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Age:</strong> Natural wear and tear over time</li>
          <li className="text-gray-600">• <strong>Obesity:</strong> Extra weight stresses joints</li>
          <li className="text-gray-600">• <strong>Injuries:</strong> Previous joint injuries increase arthritis risk</li>
          <li className="text-gray-600">• <strong>Genetics:</strong> Family history of arthritis or osteoporosis</li>
          <li className="text-gray-600">• <strong>Autoimmune Disorders:</strong> Body attacks its own joints</li>
          <li className="text-gray-600">• <strong>Lack of Exercise:</strong> Weak muscles don't support joints</li>
          <li className="text-gray-600">• <strong>Poor Nutrition:</strong> Calcium and vitamin D deficiency</li>
          <li className="text-gray-600">• <strong>Hormonal Changes:</strong> Especially in postmenopausal women</li>
          <li className="text-gray-600">• <strong>Repetitive Stress:</strong> Overuse of certain joints</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Effects and Complications</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Chronic pain and discomfort</li>
          <li className="text-gray-600">• Joint stiffness and reduced range of motion</li>
          <li className="text-gray-600">• Difficulty performing daily activities</li>
          <li className="text-gray-600">• Increased risk of falls and fractures</li>
          <li className="text-gray-600">• Joint deformity</li>
          <li className="text-gray-600">• Muscle weakness</li>
          <li className="text-gray-600">• Depression and anxiety</li>
          <li className="text-gray-600">• Sleep problems due to pain</li>
          <li className="text-gray-600">• Reduced quality of life</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Joint and Bone Health</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-indigo-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/2155e4a7cb97e860d597e291c8497181.jpeg"
                  alt="Noni Juice"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Noni Juice
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Reduces chronic pain and swelling in joints. Treats rheumatoid arthritis and helps with muscle aches and pains. Contains powerful anti-inflammatory properties that support joint health and mobility.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(156 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦32,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-emerald-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/1ac0f230ef4066a4924563e20feb1190.jpeg"
                  alt="Vita Trace"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Vita Trace (Double Stemcell+)
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Combines multiple fruit stem cells with 16 Ayurvedic remedies to repair damaged tissues and cells. Boosts immune system and provides wholistic rejuvenation to support your body's natural defense against abnormal cell growth.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(98 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦29,900</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Joint and Bone Health</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Exercise regularly (low-impact activities like swimming, walking)</li>
          <li className="text-gray-600">• Maintain a healthy weight to reduce joint stress</li>
          <li className="text-gray-600">• Eat calcium-rich foods (dairy, leafy greens, fortified foods)</li>
          <li className="text-gray-600">• Get adequate vitamin D (sunlight, supplements)</li>
          <li className="text-gray-600">• Practice good posture</li>
          <li className="text-gray-600">• Use proper body mechanics when lifting</li>
          <li className="text-gray-600">• Apply heat or cold therapy for pain relief</li>
          <li className="text-gray-600">• Try gentle stretching and yoga</li>
          <li className="text-gray-600">• Avoid smoking and excessive alcohol</li>
          <li className="text-gray-600">• Get adequate rest and sleep</li>
          <li className="text-gray-600">• Consider physical therapy</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Foods for Strong Bones and Joints</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Dairy products (milk, yogurt, cheese)</li>
          <li className="text-gray-600">• Leafy green vegetables (kale, spinach)</li>
          <li className="text-gray-600">• Fatty fish (salmon, sardines)</li>
          <li className="text-gray-600">• Nuts and seeds</li>
          <li className="text-gray-600">• Fortified foods (cereals, orange juice)</li>
          <li className="text-gray-600">• Beans and legumes</li>
          <li className="text-gray-600">• Fruits rich in vitamin C</li>
        </ul>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> While Ayurvedic remedies can help manage joint and bone problems, they should complement medical treatment. Consult with your healthcare provider for proper diagnosis and treatment, especially for severe arthritis or osteoporosis.
          </p>
        </div>

        <p className="text-gray-600 leading-relaxed mb-6">
          By combining Ayurvedic herbal support with proper nutrition, exercise, and medical care, you can effectively manage joint pain, arthritis, and osteoporosis while maintaining mobility and quality of life.
        </p>
      </>
    )
  },
  '11': {
    category: "Women's Health",
    date: 'April 5, 2025',
    readTime: '7 min read',
    title: "Women's Reproductive Health: Natural Ayurvedic Support for Hormonal Balance",
    image: 'https://readdy.ai/api/search-image?query=woman%20experiencing%20menstrual%20cramps%20lying%20on%20bed%20holding%20lower%20abdomen%20in%20discomfort%20hot%20water%20bottle%20nearby%20depicting%20hormonal%20imbalance%20and%20reproductive%20health%20issues%20realistic%20medical%20photography&width=1200&height=600&seq=blog-womens-realistic-health&orientation=landscape',
    relatedArticles: [2, 6, 7],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Women's reproductive health is complex and influenced by hormones, lifestyle, and overall well-being. Many women experience reproductive health issues at some point in their lives. Ayurvedic medicine offers natural approaches to support hormonal balance, fertility, and menstrual wellness.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Women's Health Issues</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Menstrual Irregularities:</strong> Irregular periods, heavy bleeding, or missed periods</li>
          <li className="text-gray-600">• <strong>PCOS:</strong> Polycystic ovary syndrome affecting hormones and fertility</li>
          <li className="text-gray-600">• <strong>Endometriosis:</strong> Painful condition where tissue grows outside the uterus</li>
          <li className="text-gray-600">• <strong>PMS:</strong> Premenstrual syndrome with mood swings and physical symptoms</li>
          <li className="text-gray-600">• <strong>Menopause Symptoms:</strong> Hot flashes, mood changes, sleep problems</li>
          <li className="text-gray-600">• <strong>Fertility Issues:</strong> Difficulty conceiving</li>
          <li className="text-gray-600">• <strong>Hormonal Imbalances:</strong> Affecting mood, weight, and energy</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Causes</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Hormonal imbalances (estrogen, progesterone, testosterone)</li>
          <li className="text-gray-600">• Stress and anxiety</li>
          <li className="text-gray-600">• Poor diet and nutrition</li>
          <li className="text-gray-600">• Lack of exercise or excessive exercise</li>
          <li className="text-gray-600">• Obesity or being underweight</li>
          <li className="text-gray-600">• Thyroid problems</li>
          <li className="text-gray-600">• Environmental toxins</li>
          <li className="text-gray-600">• Certain medications</li>
          <li className="text-gray-600">• Genetics</li>
          <li className="text-gray-600">• Age and natural hormonal changes</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Effects on Health and Well-being</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Painful menstrual cramps</li>
          <li className="text-gray-600">• Mood swings, irritability, and depression</li>
          <li className="text-gray-600">• Fatigue and low energy</li>
          <li className="text-gray-600">• Weight gain or difficulty losing weight</li>
          <li className="text-gray-600">• Acne and skin problems</li>
          <li className="text-gray-600">• Hair loss or excessive hair growth</li>
          <li className="text-gray-600">• Difficulty conceiving</li>
          <li className="text-gray-600">• Sleep disturbances</li>
          <li className="text-gray-600">• Reduced libido</li>
          <li className="text-gray-600">• Impact on relationships and quality of life</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Women's Health</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-emerald-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/1ac0f230ef4066a4924563e20feb1190.jpeg"
                  alt="Vita Trace"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Vita Trace (Double Stemcell+)
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Supports reproductive system and helps with hormonal balance. Provides wholistic rejuvenation for women's health concerns, repairs damaged tissues, and supports overall reproductive wellness.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(98 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦29,900</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-rose-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6e120bfc8043ea77825d5145e57e2953.jpeg"
                  alt="Clean Detox"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Clean Detox
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Helps detoxify the body and eliminate toxins that can affect hormonal balance. Supports liver function which is crucial for hormone metabolism and overall reproductive health.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(127 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦28,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Women's Health</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Eat a balanced diet rich in whole foods, fruits, and vegetables</li>
          <li className="text-gray-600">• Exercise regularly (moderate intensity, 30 minutes daily)</li>
          <li className="text-gray-600">• Maintain a healthy weight</li>
          <li className="text-gray-600">• Manage stress through yoga, meditation, or relaxation</li>
          <li className="text-gray-600">• Get adequate sleep (7-9 hours per night)</li>
          <li className="text-gray-600">• Limit caffeine and alcohol</li>
          <li className="text-gray-600">• Avoid smoking</li>
          <li className="text-gray-600">• Stay hydrated</li>
          <li className="text-gray-600">• Track your menstrual cycle</li>
          <li className="text-gray-600">• Get regular gynecological check-ups</li>
          <li className="text-gray-600">• Practice self-care and prioritize your well-being</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Foods for Hormonal Balance</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Leafy green vegetables</li>
          <li className="text-gray-600">• Fatty fish (omega-3)</li>
          <li className="text-gray-600">• Nuts and seeds (especially flaxseeds)</li>
          <li className="text-gray-600">• Whole grains</li>
          <li className="text-gray-600">• Legumes and beans</li>
          <li className="text-gray-600">• Fermented foods (yogurt, kefir)</li>
          <li className="text-gray-600">• Berries and antioxidant-rich fruits</li>
          <li className="text-gray-600">• Healthy fats (avocado, olive oil)</li>
        </ul>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> While Ayurvedic remedies can support women's reproductive health, they should complement medical care. Consult with your healthcare provider for proper diagnosis and treatment of reproductive health issues.
          </p>
        </div>

        <p className="text-gray-600 leading-relaxed mb-6">
          By combining Ayurvedic herbal support with healthy lifestyle practices and medical care, women can achieve hormonal balance, improve reproductive health, and enhance overall well-being.
        </p>
      </>
    )
  },
  '12': {
    category: 'Digestive Health',
    date: 'April 7, 2025',
    readTime: '6 min read',
    title: 'Natural Relief for Piles (Hemorrhoids): Ayurvedic Treatment and Prevention',
    image: 'https://readdy.ai/api/search-image?query=person%20sitting%20uncomfortably%20on%20cushion%20with%20visible%20discomfort%20depicting%20hemorrhoids%20and%20digestive%20health%20issues%20realistic%20medical%20photography%20natural%20lighting%20emphasizing%20digestive%20concern&width=1200&height=600&seq=blog-piles-realistic-discomfort&orientation=landscape',
    relatedArticles: [7, 9, 3],
    content: (
      <>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Piles (hemorrhoids) are swollen veins in the lower rectum and anus that can cause pain, bleeding, and discomfort. This common condition affects millions of people but is often not discussed due to embarrassment. Ayurvedic medicine offers natural remedies to treat piles and prevent recurrence.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Understanding Piles (Hemorrhoids)</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          Hemorrhoids are swollen blood vessels in the rectal area. They can be internal (inside the rectum) or external (under the skin around the anus). While not usually serious, they can be very uncomfortable and affect quality of life.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Common Causes of Piles</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• <strong>Chronic Constipation:</strong> Straining during bowel movements</li>
          <li className="text-gray-600">• <strong>Chronic Diarrhea:</strong> Frequent loose stools</li>
          <li className="text-gray-600">• <strong>Low Fiber Diet:</strong> Lack of dietary fiber</li>
          <li className="text-gray-600">• <strong>Pregnancy:</strong> Increased pressure on pelvic veins</li>
          <li className="text-gray-600">• <strong>Obesity:</strong> Extra weight increases pressure</li>
          <li className="text-gray-600">• <strong>Prolonged Sitting:</strong> Especially on the toilet</li>
          <li className="text-gray-600">• <strong>Heavy Lifting:</strong> Repeated heavy lifting</li>
          <li className="text-gray-600">• <strong>Age:</strong> Risk increases with age</li>
          <li className="text-gray-600">• <strong>Genetics:</strong> Family history</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Symptoms of Piles</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Painless bleeding during bowel movements</li>
          <li className="text-gray-600">• Itching or irritation in the anal area</li>
          <li className="text-gray-600">• Pain or discomfort, especially when sitting</li>
          <li className="text-gray-600">• Swelling around the anus</li>
          <li className="text-gray-600">• A lump near the anus (may be painful)</li>
          <li className="text-gray-600">• Leakage of feces</li>
        </ul>

        <div className="bg-teal-50 rounded-2xl p-8 my-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Natural Solutions for Piles</h3>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-indigo-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/2155e4a7cb97e860d597e291c8497181.jpeg"
                  alt="Noni Juice"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  PRIMARY SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Noni Juice
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Treats piles and inflammatory conditions of the digestive system. Helps with constipation, dysentery, and accelerates wound healing in the digestive tract. Contains Aloe vera for soothing and healing properties.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(156 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦32,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-rose-50 p-8 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6e120bfc8043ea77825d5145e57e2953.jpeg"
                  alt="Clean Detox"
                  className="w-full h-auto object-contain max-w-xs"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  SUPPORTING SOLUTION
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                  Clean Detox
                </h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Stimulates digestive fire and promotes bile flow. Helps with constipation and supports healthy digestion, which is crucial for preventing and treating piles.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(127 reviews)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">₦28,000</span>
                  <span className="text-teal-600 font-medium group-hover:underline flex items-center gap-2">
                    View Product
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Lifestyle Tips for Piles Prevention and Relief</h2>
        <ul className="space-y-3 mb-6 ml-6">
          <li className="text-gray-600">• Eat a high-fiber diet (fruits, vegetables, whole grains)</li>
          <li className="text-gray-600">• Drink plenty of water (8-10 glasses daily)</li>
          <li className="text-gray-600">• Exercise regularly to prevent constipation</li>
          <li className="text-gray-600">• Don't strain during bowel movements</li>
          <li className="text-gray-600">• Go to the bathroom when you feel the urge</li>
          <li className="text-gray-600">• Don't sit on the toilet for long periods</li>
          <li className="text-gray-600">• Take warm sitz baths for relief</li>
          <li className="text-gray-600">• Use moist towelettes instead of dry toilet paper</li>
          <li className="text-gray-600">• Apply ice packs to reduce swelling</li>
          <li className="text-gray-600">• Avoid heavy lifting</li>
          <li className="text-gray-600">• Maintain a healthy weight</li>
          <li className="text-gray-600">• Avoid prolonged sitting</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Foods to Eat and Avoid</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Foods to Eat:</h3>
            <ul className="space-y-2 ml-6">
              <li className="text-gray-600">• Whole grains (oats, brown rice)</li>
              <li className="text-gray-600">• Fruits (apples, pears, berries)</li>
              <li className="text-gray-600">• Vegetables (broccoli, carrots)</li>
              <li className="text-gray-600">• Legumes (beans, lentils)</li>
              <li className="text-gray-600">• Prunes and prune juice</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Foods to Avoid:</h3>
            <ul className="space-y-2 ml-6">
              <li className="text-gray-600">• Processed foods</li>
              <li className="text-gray-600">• Spicy foods</li>
              <li className="text-gray-600">• Alcohol</li>
              <li className="text-gray-600">• Caffeine</li>
              <li className="text-gray-600">• Low-fiber foods</li>
            </ul>
          </div>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> While Ayurvedic remedies can help treat piles, severe cases may require medical intervention. If you experience severe pain, excessive bleeding, or symptoms that don't improve, consult a healthcare provider.
          </p>
        </div>

        <p className="text-gray-600 leading-relaxed mb-6">
          By combining Ayurvedic herbal support with dietary changes and healthy habits, you can effectively treat piles and prevent recurrence naturally.
        </p>
      </>
    )
  },
};

const allArticleIds = Object.keys(blogArticles).map(Number);

const getRelatedArticles = (currentId: number, relatedIds: number[]) => {
  return relatedIds
    .filter((id) => id !== currentId && blogArticles[id.toString()])
    .slice(0, 3)
    .map((id) => ({
      id,
      ...blogArticles[id.toString()],
    }));
};

export default function BlogDetailPage() {
  const { id } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatsappNumber = '+2348154358746';
  const whatsappMessage =
    "Hello! I'm interested in learning more about your herbal products.";

  const article =
    blogArticles[id as keyof typeof blogArticles] || blogArticles['1'];
  const currentId = Number(id) || 1;
  const relatedArticles = getRelatedArticles(currentId, article.relatedArticles || []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/8e2a19642137d9fb9670ecec43a2e980.jpeg" 
                alt="Organic Natural Solution Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                About
              </Link>
              <Link
                to="/products"
                className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Products
              </Link>
              <Link
                to="/testimonials"
                className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Testimonials
              </Link>
              <Link
                to="/blog"
                className="text-sm font-medium text-teal-600 transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Blog
              </Link>
              <Link
                to="/contact"
                className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Contact
              </Link>
              <CartButton />
            </div>
            <div className="flex md:hidden items-center gap-4">
              <CartButton />
              <button
                className="cursor-pointer"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <i
                  className={`${
                    mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                  } text-2xl text-gray-900`}
                ></i>
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-6 py-4 space-y-4">
              <Link
                to="/"
                className="block text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="block text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                About
              </Link>
              <Link
                to="/products"
                className="block text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                Products
              </Link>
              <Link
                to="/testimonials"
                className="block text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                Testimonials
              </Link>
              <Link
                to="/blog"
                className="block text-sm font-medium text-teal-600 transition-colors"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                Blog
              </Link>
              <Link
                to="/contact"
                className="block text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Article Content */}
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-teal-600 font-medium mb-8 hover:underline cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Blog
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm font-medium text-teal-600">{article.category}</span>
              <span className="text-sm text-gray-400">{article.date}</span>
              <span className="text-sm text-gray-400">{article.readTime}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">{article.title}</h1>
          </div>

          <div className="rounded-3xl overflow-hidden mb-12">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover object-center"
            />
          </div>

          <div className="prose prose-lg max-w-none">{article.content}</div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-8 border-t border-gray-200">
              <h3 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/blog/${rel.id}`}
                    className="group cursor-pointer"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    <div className="rounded-2xl overflow-hidden mb-4">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-48 object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="text-xs font-medium text-teal-600 mb-2 block">
                      {rel.category}
                    </span>
                    <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {rel.title}
                    </h4>
                    <span className="text-teal-600 font-medium group-hover:underline">
                      Read More →
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Contact / CTA */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="bg-teal-50 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Get in Touch</h3>
              <p className="text-gray-600 mb-6">
                Have questions or want to learn more about our products? Contact us today!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
                    whatsappMessage
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-full font-medium hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  WhatsApp Us
                </a>
                <a
                  href="mailto:healthyliving.hello@gmail.com"
                  className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line text-xl"></i>
                  Email Us
                </a>
                <a
                  href="tel:+2348154358746"
                  className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl"></i>
                  Call Us
                </a>
              </div>
            </div>

            <Link
              to="/products"
              className="inline-block bg-teal-600 text-white px-10 py-4 rounded-full font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Shop Our Products
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <img src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/8e2a19642137d9fb9670ecec43a2e980.jpeg" alt="Organic Natural Solution Logo" className="h-12 w-auto mb-6" />
              <p className="text-gray-400 leading-relaxed mb-6">
                Dedicated to providing the highest quality herbal remedies that honor traditional wisdom while meeting modern wellness needs.
              </p>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/healthyliving.products?igsh=NGl6a3oxZXhrcmxr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-full hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="https://www.facebook.com/share/1CKjLukjVr/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-full hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
              </div>
            </div>

            <div>
              <div className="mb-8">
                <h4 className="text-xs font-bold uppercase mb-3">LOCATION</h4>
                <p className="text-gray-400">#2 Allen Avenue Near Tonyin Roundabout, Ikeja, Lagos - Nigeria</p>
              </div>
              <div className="mb-8">
                <h4 className="text-xs font-bold uppercase mb-3">PHONE</h4>
                <p className="text-gray-400">+234 815 435 8746</p>
                <p className="text-gray-400">+234 913 789 7299</p>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase mb-3">EMAIL</h4>
                <p className="text-gray-400">healthyliving.hello@gmail.com</p>
              </div>
            </div>

            <div>
              <div className="rounded-2xl overflow-hidden h-80">
                <img 
                  src="https://static.readdy.ai/image/da62b21baa3da4233dc32d5671f36223/6686b80771a03e2f4f8c9b19d31144a1.jpeg" 
                  alt="Organic Natural Solution" 
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <p className="text-sm text-gray-400 text-center">© 2025 Organic Natural Solution. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={() => window.open(`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`, '_blank')}
        className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-2xl flex items-center justify-center z-50 transition-all hover:scale-110 cursor-pointer"
        aria-label="Contact us on WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl"></i>
      </button>
    </div>
  );
}
